import { useEffect, useMemo, useRef, useState } from "react";

import { toPx } from "~/utils/components";
import { setPageStatusBarStyle } from "~/utils/events";
import { formatDuration, formatPercentage } from "~/utils/helpers";
import { pathFor } from "~/utils/url";
import { useLabels } from "~/hooks/useLabels";
import { useBreakpoints } from "~/hooks/useBreakpoints";
import { RouteType } from "~/constants";

import { Icon } from "../icon/Icon";
import { ProgressBar } from "../progressBar/ProgressBar";
import { Text } from "../text/Text";
import { Heading } from "../heading/Heading";
import { Button } from "../button/Button";
import { Breadcrumbs } from "../breadcrumbs/Breadcrumbs";
import { Pill } from "../pill/Pill";

import "./ContentHero.css";

import type { ReactNode } from "react";
import type { ActionProp } from "../components";
import type { ButtonProps } from "../button/Button";
import type { Breadcrumb } from "../breadcrumbs/Breadcrumbs";

export interface ContentHeroProps {
  actions?: ActionProp[];
  artwork?: string;
  artworkColor?: string;
  breadcrumbs?: Breadcrumb[];
  children?: ReactNode;
  description?: string;
  duration?: number;
  notebookHref?: string;
  postTitle?: ReactNode;
  preTitle?: ReactNode;
  progress?: number;
  title?: string;
  variant?: "default" | "mobile-native";
  onNotebookClick?: () => void;
}

const ARTWORK_HEIGHT = 225;

const DEFAULT_LABELS = {
  "common.cta.back": "Back",
  "common.cta.notebook": "Class Notebook",
};

export function getHeaderButtonProps(
  props: Partial<ContentHeroProps>,
  { isHeaderPinned }: { isHeaderPinned: boolean },
): Partial<ButtonProps> {
  if (isHeaderPinned) {
    return {
      appearance: "minimal",
    };
  }

  return {
    appearance: "primary",
  };
}

export function ContentHero({
  actions,
  artwork,
  artworkColor,
  breadcrumbs,
  children,
  description,
  duration,
  notebookHref,
  postTitle,
  preTitle,
  progress,
  title,
  variant = "default",
  onNotebookClick,
}: ContentHeroProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useLabels({ fallback: DEFAULT_LABELS });
  const breakpoints = useBreakpoints();

  const [isHeaderPinned, setIsHeaderPinned] = useState(false);

  const isMobileNative = variant === "mobile-native";

  const customProperties = {
    "--hero-artwork-height": toPx(ARTWORK_HEIGHT),
    "--hero-artwork-color": artworkColor,
  };

  const isDesktopLayout = breakpoints.md;

  const previousPageHref = breadcrumbs?.[breadcrumbs.length - 1].href;

  const isComplete = Boolean(progress && progress >= 1);

  const backButtonIcon =
    isMobileNative &&
    previousPageHref &&
    [pathFor(RouteType.HOME), pathFor(RouteType.CLASSROOM)].includes(
      previousPageHref,
    )
      ? "angle-down"
      : "angle-left";

  const headerButtonCommonProps = getHeaderButtonProps(
    { artworkColor },
    { isHeaderPinned },
  );

  const progressLabel = useMemo(() => {
    if (duration && (!progress || (progress && progress >= 1))) {
      return formatDuration(duration, t);
    }

    return progress ? `${formatPercentage(progress)} Complete` : "";
  }, [duration, progress, t]);

  // Pin the header when scrolled
  useEffect(() => {
    // Only run when in app
    if (!isMobileNative) return;

    // Don't pin on large screens
    if (isDesktopLayout) {
      setIsHeaderPinned(false);
      return;
    }

    let requestRunning: number | null = null;

    function handleScroll() {
      if (requestRunning === null) {
        requestRunning = window.requestAnimationFrame(() => {
          const top = ref.current?.getBoundingClientRect().top ?? 0;

          setIsHeaderPinned(Math.abs(top) > ARTWORK_HEIGHT);

          requestRunning = null;
        });
      }
    }

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDesktopLayout, isMobileNative, ref]);

  useEffect(
    () => setPageStatusBarStyle(isHeaderPinned ? "dark" : "light"),
    [isHeaderPinned],
  );

  return (
    <div
      className="ContentHero"
      data-header-pinned={isHeaderPinned}
      ref={ref}
      style={customProperties}
    >
      <div className="ContentHero__artwork">
        <img src={artwork} decoding="async" alt="" />
      </div>

      <div className="ContentHero__panel">
        {breadcrumbs && (
          <div className="ContentHero__breadcrumbs">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        )}
        <Text
          className="ContentHero__pre-title"
          preset="pretitle"
          color="inherit"
          size="md"
          weight="semibold"
        >
          {preTitle}
        </Text>
        <Heading level="1">{title}</Heading>
        <Text size="md" weight="semibold" className="ContentHero__post-title">
          {postTitle}
        </Text>
        {Boolean(actions && actions.length > 0) && (
          <div className="ContentHero__actions">
            {actions?.map((action) => (
              <Pill
                key={action.text}
                icon={action.icon}
                iconVariant={action.iconVariant}
                onClick={action.onClick}
              >
                {action.text}
              </Pill>
            ))}
          </div>
        )}

        {description && (
          <Text as="p" style={{ margin: "var(--size-1) 0" }}>
            {description}
          </Text>
        )}

        <div className="ContentHero__progress">
          <div className="ContentHero__progress-label">
            <Text size="sm" weight="semibold">
              {progressLabel}
            </Text>

            {isComplete && (
              <Icon
                icon="circle-check"
                variant="solid"
                size="sm"
                color="var(--color-brand-accessible)"
              />
            )}
          </div>

          <ProgressBar value={progress ?? 0} />
        </div>

        <div className="ContentHero__footer">{children}</div>
      </div>

      {
        // Put the header at the bottom so that keyboard
        // tabbing focuses on the panel content first
      }
      <div className={"ContentHero__header"}>
        <Button
          {...headerButtonCommonProps}
          href={previousPageHref}
          shape="circle"
          icon={backButtonIcon}
          nativeBehavior="pop"
          label={t("common.cta.back")}
          hidden={isDesktopLayout || !isMobileNative}
        />

        <div
          className="ContentHero__header-title"
          aria-hidden={!isHeaderPinned}
          hidden={isDesktopLayout || !isMobileNative}
        >
          {title}
        </div>

        <Button
          {...headerButtonCommonProps}
          disabled={!notebookHref}
          href={notebookHref}
          shape={!isDesktopLayout ? "circle" : undefined}
          icon="notebook"
          iconVariant="solid"
          nativeBehavior="present"
          label={t("common.cta.notebook")}
          nativeStencil={{
            propsJSON: "{}",
            type: "notebook",
          }}
          onClick={onNotebookClick}
        >
          {isDesktopLayout && t("common.cta.notebook")}
        </Button>
      </div>
    </div>
  );
}
